/**
 * Opening and saving a text file, the same way from the page's side in a browser and in the
 * desktop app.
 *
 * - **desktop** — the Rust broker's native dialogs; it writes only paths a dialog handed out.
 * - **browser with the File System Access API** (Chrome, Edge) — a handle to the real file, so
 *   "save" writes it in place.
 * - **any other browser** — a file input to open, and a download to save.
 */

import { isDesktop } from './desktop'

/** An open file. `handle` is the browser's way back to it; `path` the desktop's. */
export interface OpenFile {
  name: string
  content: string
  path?: string
  handle?: FileSystemFileHandle
  /** How to write it back, for a file opened from the Explorer (a repository, a workspace folder). */
  writer?: (content: string) => Promise<void>
}

/** Why a file could not be opened or saved, as a translation key's last part. */
export type FileError = 'too-large' | 'not-text' | 'not-granted' | 'failed'

export class FileProblem extends Error {
  constructor(readonly reason: FileError) {
    super(reason)
  }
}

/** Same limit as the desktop broker's, so a file behaves the same wherever it is opened. */
export const MAX_BYTES = 5 * 1024 * 1024

interface PickerWindow {
  showOpenFilePicker?: (options?: object) => Promise<FileSystemFileHandle[]>
  showSaveFilePicker?: (options?: object) => Promise<FileSystemFileHandle>
}

const picker = (): PickerWindow => window as unknown as PickerWindow

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke: call } = await import('@tauri-apps/api/core')
  try {
    return await call<T>(command, args)
  } catch (error) {
    const reason = String(error)
    throw new FileProblem(reason === 'too-large' || reason === 'not-text' || reason === 'not-granted' ? reason : 'failed')
  }
}

const isAbort = (error: unknown) => error instanceof DOMException && error.name === 'AbortError'

/** For a File without arrayBuffer(): older webviews, and the test environment. */
function readWithFileReader(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = () => reject(new FileProblem('failed'))
    reader.readAsArrayBuffer(file)
  })
}

/** Reads a File as text, refusing what is too big or is not text. */
export async function readText(file: File): Promise<string> {
  if (file.size > MAX_BYTES) throw new FileProblem('too-large')
  const bytes = new Uint8Array(typeof file.arrayBuffer === 'function' ? await file.arrayBuffer() : await readWithFileReader(file))
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    throw new FileProblem('not-text')
  }
}

/** A file input, for browsers without the File System Access API. Resolves null when cancelled. */
function pickWithInput(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.onchange = () => resolve(input.files?.[0] ?? null)
    input.addEventListener('cancel', () => resolve(null))
    input.click()
  })
}

/** Asks for a file. Null when the person cancelled. */
export async function openFile(): Promise<OpenFile | null> {
  if (isDesktop()) {
    return invoke<{ path: string; name: string; content: string } | null>('file_open')
  }
  if (picker().showOpenFilePicker) {
    try {
      const [handle] = await picker().showOpenFilePicker!()
      if (!handle) return null
      const file = await handle.getFile()
      return { name: file.name, content: await readText(file), handle }
    } catch (error) {
      if (isAbort(error)) return null
      throw error
    }
  }
  const file = await pickWithInput()
  return file ? { name: file.name, content: await readText(file) } : null
}

/** Whether "save" can write the file back where it came from, rather than download a copy. */
export function canSaveInPlace(file: OpenFile): boolean {
  return Boolean(file.writer || file.path || file.handle)
}

async function writeHandle(handle: FileSystemFileHandle, content: string) {
  const writable = await (handle as FileSystemFileHandle & { createWritable(): Promise<FileSystemWritableFileStream> }).createWritable()
  await writable.write(content)
  await writable.close()
}

function download(name: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Asks where to save and writes there. Returns the file as it now is — with its new name and
 * way back — or null when the person cancelled. A browser without the API downloads a copy.
 */
export async function saveFileAs(file: OpenFile, content: string): Promise<OpenFile | null> {
  if (isDesktop()) {
    const saved = await invoke<{ path: string; name: string } | null>('file_save_as', { suggested: file.name, content })
    return saved ? { ...saved, content } : null
  }
  if (picker().showSaveFilePicker) {
    try {
      const handle = await picker().showSaveFilePicker!({ suggestedName: file.name })
      await writeHandle(handle, content)
      return { name: handle.name, content, handle }
    } catch (error) {
      if (isAbort(error)) return null
      throw error
    }
  }
  download(file.name, content)
  return { name: file.name, content }
}

/** Writes the file back where it came from; without a way back, it is a "save as". */
export async function saveFile(file: OpenFile, content: string): Promise<OpenFile | null> {
  if (new Blob([content]).size > MAX_BYTES) throw new FileProblem('too-large')
  if (file.writer) {
    await file.writer(content)
    return { ...file, content }
  }
  if (file.path && isDesktop()) {
    await invoke('file_save', { path: file.path, content })
    return { ...file, content }
  }
  if (file.handle) {
    await writeHandle(file.handle, content)
    return { ...file, content }
  }
  return saveFileAs(file, content)
}

/**
 * A file for the agent to work on (a super skill's "arquivo deste computador", docs/refactor/50): only its path, from
 * the native dialog — the app never reads it. Desktop only: on the web the agent runs on another computer, whose
 * files the browser cannot point at. Null when cancelled.
 */
export async function pickFilePath(extensions: string[]): Promise<string | null> {
  if (!isDesktop()) return null
  return invoke<string | null>('file_pick_path', { extensions })
}
